// [목적] /login 분기 컨테이너 — useIsDesktop 으로 데스크톱/모바일 레이아웃을 조건부 렌더한다.
// [이유] Wave 1 에서 LoginMobileLayout / LoginDesktopLayout / OAuthButtonStack 으로 분리됨에 따라
//         본 파일은 OAuth 핸들러 로직(R-8 보호)과 분기 판단만을 담당하도록 축소한다.
//         WC-FE-AUTH-04: handleOAuthClick 을 mock navigate 에서 useAuth().login(provider, returnTo) 로 교체.
//         2026-05-16 reauth-redesign — 디자인 원본에 "둘러보기" CTA 없음 → onAnonBrowse prop 제거.
//         Apple 버튼은 디자인 원본에 활성으로 표기되어 있으나 BE 미구현 → onClick 에서 안내 Snackbar.
// [사이드 이팩트]
//   - searchParams 의 returnTo / error 처리 추가.
//   - error 쿼리 파라미터가 있으면 인라인 에러 메시지 Toast(MUI Snackbar) 로 표시.
//   - Apple onClick → "곧 지원됩니다" Snackbar (BE 미구현 보호).
//   - onAnonBrowse 핸들러 제거 — 디자인 원본 비포함.
// [수정 시 고려사항]
//   - handleOAuthClick 내부만 수정 — 레이아웃 영향 없음 (R-8).
//   - returnTo deep-link: searchParams.get('returnTo') → useAuth().login() 에 전달.
//   - 분기 임계값 변경 시: useIsDesktop.ts (1024px) 와 동기화.
//   - R-8 주의: onClick 핸들러 로직을 LoginDesktopLayout / LoginMobileLayout 으로 이동 금지.
//   - Apple BE 구현 시 setToastMessage 분기 제거하고 login('apple', returnTo) 직호출로 교체.

import { Snackbar } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useIsDesktop } from '../../hooks/useIsDesktop.js';
import { useAuth } from '../../hooks/useAuth.js';
import { LoginDesktopLayout } from './LoginDesktopLayout.js';
import { LoginMobileLayout } from './LoginMobileLayout.js';
import type { OAuthProvider } from './OAuthButtonStack.js';

// ─── 에러 코드 → 사용자 메시지 매핑 ─────────────────────────────────────────
// [이유] Q-6 결정: 에러 코드 그대로 노출. i18n 매핑은 후속 i18n-error-mapping Spec.
//         LGO-FE-02(auth-logout-hardening Wave 3): auth.SESSION_EXPIRED / auth.SESSION_REVOKED 추가.
//           자동 로그아웃 사유 구분 — 유휴/세션 만료 vs 강제 폐기(도난·탈퇴·버전 무효화).
//         비차단 Snackbar 경유 — 차단 모달 금지, 무음 금지(D-LGO-AUTOLOGOUT).
export function getErrorMessage(code: string): string {
  const messages: Record<string, string> = {
    'auth.STATE_INVALID': '로그인 상태가 유효하지 않습니다. 다시 시도해주세요.',
    'auth.SESSION_HYDRATE_FAILED': '로그인 세션을 불러오지 못했습니다. 다시 시도해주세요.',
    'auth.OAUTH_ACCOUNT_CONFLICT': '이미 다른 방법으로 가입된 계정입니다.',
    'auth.PROVIDER_ERROR': '소셜 로그인 처리 중 오류가 발생했습니다.',
    // LGO-FE-02: 자동 로그아웃 사유 구분 메시지
    'auth.SESSION_EXPIRED': '세션이 만료되어 로그아웃되었습니다. 다시 로그인해 주세요.',
    'auth.SESSION_REVOKED': '보안을 위해 로그아웃되었습니다. 다시 로그인해 주세요.',
  };
  return messages[code] ?? `로그인 중 오류가 발생했습니다. (${code})`;
}

/**
 * /login 분기 컨테이너.
 * useIsDesktop() → true : LoginDesktopLayout
 *                → false: LoginMobileLayout
 * OAuth 핸들러와 onAnonBrowse 는 이 컴포넌트가 소유한다 (R-8).
 * WC-FE-AUTH-04: handleOAuthClick 이 useAuth().login(provider, returnTo) 호출.
 */
export function LoginPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const { login } = useAuth();

  const returnTo = searchParams.get('returnTo') ?? undefined;
  const errorCode = searchParams.get('error');

  // Toast 상태
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // error 쿼리 파라미터 → Toast 표시
  // [2026-06-01] 한 번 표시 후 URL 의 error 파라미터를 제거 — 새로고침/재마운트 시 Snackbar 재등장 방지.
  //   카카오 동의 화면 뒤로가기처럼 사용자가 의도적으로 취소한 경우 BE 콜백이 ?error=auth.OAUTH_FAILED 를 붙여
  //   /login 으로 보내는데, 이 파라미터가 URL 에 잔존하면 페이지 mount 마다 메시지가 반복 노출되는 UX 결함이 발생.
  useEffect(() => {
    if (errorCode) {
      setToastMessage(getErrorMessage(errorCode));
      setToastOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('error');
      setSearchParams(next, { replace: true });
    }
    // searchParams/setSearchParams 는 의도적으로 의존성에서 제외 —
    // errorCode 가 한 번 비워지면 재실행되지 않게 하여 무한 setState 루프 방지.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  /**
   * OAuth 버튼 클릭 — BE getAuthorizeUrl() → window.location.assign() (실 IdP redirect)
   * [수정: 디자인 개편] Apple은 OAuthProvider 유니온에서 자체가 제거되어 이 함수가 받는 provider 는 항상 kakao|naver|google.
   */
  const handleOAuthClick = (provider: OAuthProvider): void => {
    void login(provider, returnTo);
  };

  return (
    <>
      {isDesktop ? (
        <LoginDesktopLayout onProviderClick={handleOAuthClick} />
      ) : (
        <LoginMobileLayout onProviderClick={handleOAuthClick} />
      )}

      {/* 에러/알림 Snackbar */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
